import { searchInCloudStore } from "@api/Search"
import CSInput from "@components/Form/CSInput"
import SectionGroup from "@components/SectionGroup/SectionGroup"
import useDebounce from "@hooks/UseDebounce"
import Localization from "@localization/Index"
import { PageStates } from "@utils/PageStateUtils"
import { SearchResult, SearchResultTypeEnum } from "cs.node.utils/SearchUtils"
import { useEffect, useState } from "react"
import Spinner from "react-bootstrap/esm/Spinner"
import { useLocation } from "react-router-dom"
import HeaderSearchBarResult from "./HeaderSearchBarResult"
import HeaderSearchBarResultFilter from "./HeaderSearchBarResultComponents.tsx/HeaderSearchBarResultFilter"

let controller: AbortController = new AbortController()

type Props = {
    onSearchToggle?: (isOpen: boolean) => unknown
}

const HeaderSearchBar = (props: Props) => {
    const { onSearchToggle } = props
    const [componentState, setComponentState] = useState<PageStates>(PageStates.IDLE)
    const [isSearchResultsOpen, setIsSearchResultsOpen] = useState(false)
    const [searchTerm,setSearchTerm] = useState<string>("")
    const searchTermDebounced = useDebounce<string>(searchTerm, 700)
    const [results, setResults] = useState<SearchResult[]>()
    const [resultTypeFilter, setResultTypeFilter] = useState<SearchResultTypeEnum>()
    const location = useLocation()

    const toggleIsSearchOpen = (isOpen: boolean) =>{
        setIsSearchResultsOpen(isOpen)
        onSearchToggle?.(isOpen)
    }

    const doSearch = async()=>{
        controller.abort()
        controller = new AbortController()
        if(searchTerm.length < 3) {
            setResults(undefined)
            return setComponentState(PageStates.IDLE)
        }
        setComponentState(PageStates.DATA_LOADING)
        try{
            const response = await searchInCloudStore(searchTerm, controller)
            setResults(response.results)
            return setComponentState(PageStates.DATA_SHOWN)
        } catch(err:any) {
            if(err.name === "CanceledError") {
                return 
            }
            setResults(undefined)
            return setComponentState(PageStates.DATA_LOADING_ERROR)
        }
    }

    useEffect(() => {
        doSearch()
    }, [searchTermDebounced])

    useEffect(()=>{
        toggleIsSearchOpen(false)
    }, [location.pathname, location.search, location.hash])

    const itemResults = results?.filter(r=>r.type === SearchResultTypeEnum.Item) ?? []
    const orderResults = results?.filter(r=>r.type === SearchResultTypeEnum.Order) ?? []
    const shopResults = (results?.filter(r=>r.type === SearchResultTypeEnum.Shop) ?? [])
    const customerResults = results?.filter(r=>r.type === SearchResultTypeEnum.Customer) ?? []

    return <>
    <div className="position-relative w-100" style={{zIndex: isSearchResultsOpen? 9999: undefined, maxWidth: "1200px"}}>
        <CSInput
            controlId='header_search'
            placeholder={Localization.CERCA}
            type="text"
            icon="bi-search"
            value={searchTerm}
            onChange={(e) =>{
                const value = e.target.value
                setResultTypeFilter(undefined)
                if(value.length === 0) {
                    setResults(undefined)
                    setComponentState(PageStates.IDLE)
                    toggleIsSearchOpen(false)
                    return setSearchTerm("")
                }
                toggleIsSearchOpen(true)
                return setSearchTerm(e.target.value)
            }}
            width={'100%'}
            autoComplete="off"
            className={isSearchResultsOpen ? "shadow shadow-lg" : undefined}
            onClear={()=>{
                setResultTypeFilter(undefined)
                setSearchTerm("")
                setResults(undefined)
                toggleIsSearchOpen(false)
                setComponentState(PageStates.IDLE)
            }}
            onFocus={()=>{
                toggleIsSearchOpen(true)
            }}
        />
        {isSearchResultsOpen && (
            <div className="w-100 position-absolute d-flex align-items-center justify-content-center bg-white border" style={{borderRadius: ".75rem", top: "calc(100% + 1rem)"}}>
                    {componentState === PageStates.IDLE && 
                        <div className="w-100 d-flex align-items-center justify-content-center p-5">
                            <p>{Localization.REACT_SELECT.DIGITARE_CARATTERI_RICERCA(3)}</p>
                        </div>
                    }
                    {componentState === PageStates.DATA_LOADING && 
                        <div className="w-100 d-flex align-items-center justify-content-center p-5">
                            <Spinner animation="border" variant="primary" style={{width: '40px', height: "40px"}} />
                        </div>
                    }
                    {componentState === PageStates.DATA_LOADING_ERROR &&
                        <div className="w-100 d-flex align-items-center justify-content-center p-5">
                            <p>{Localization.ERRORE_GENERICO}</p>
                        </div>
                    }
                    {componentState === PageStates.DATA_SHOWN && results && results.length === 0 &&
                        <div className="w-100 d-flex align-items-center justify-content-center p-5">
                            <p>{Localization.NESSUN_RISULTATO_TROVATO}</p>
                        </div>
                    }
                    {componentState === PageStates.DATA_SHOWN && results && results.length > 0 &&
                        <div className="w-100 d-flex align-items-start justify-content-start">
                            <div className="w-100 d-flex flex-column align-items-start justify-content-start" style={{paddingLeft: "1.2rem", paddingRight: "1.2rem"}}>
                                <div className="w-100 d-flex align-items-center justify-content-start">
                                    {itemResults.length > 0 && <HeaderSearchBarResultFilter selected={resultTypeFilter === SearchResultTypeEnum.Item} type={SearchResultTypeEnum.Item} count={itemResults.length} onClick={()=>{
                                        if(resultTypeFilter === SearchResultTypeEnum.Item) {
                                            setResultTypeFilter(undefined)
                                        } else{
                                            setResultTypeFilter(SearchResultTypeEnum.Item)
                                        }
                                    }} />}
                                    {orderResults.length > 0 && <HeaderSearchBarResultFilter selected={resultTypeFilter === SearchResultTypeEnum.Order} type={SearchResultTypeEnum.Order} count={orderResults.length} onClick={()=>{
                                        if(resultTypeFilter === SearchResultTypeEnum.Order) {
                                            setResultTypeFilter(undefined)
                                        } else{
                                            setResultTypeFilter(SearchResultTypeEnum.Order)
                                        }
                                    }} />}
                                    {shopResults.length > 0 && <HeaderSearchBarResultFilter selected={resultTypeFilter === SearchResultTypeEnum.Shop} type={SearchResultTypeEnum.Shop} count={shopResults.length} onClick={()=>{
                                        if(resultTypeFilter === SearchResultTypeEnum.Shop) {
                                            setResultTypeFilter(undefined)
                                        } else{
                                            setResultTypeFilter(SearchResultTypeEnum.Shop)
                                        }
                                    }} />}
                                    {customerResults.length > 0 && <HeaderSearchBarResultFilter selected={resultTypeFilter === SearchResultTypeEnum.Customer} type={SearchResultTypeEnum.Customer} count={customerResults.length} onClick={()=>{
                                        if(resultTypeFilter === SearchResultTypeEnum.Customer) {
                                            setResultTypeFilter(undefined)
                                        } else{
                                            setResultTypeFilter(SearchResultTypeEnum.Customer)
                                        }
                                    }} />}
                                </div>
                                <div className="w-100" style={{overflowY: "auto", maxHeight: "600px"}}>
                                    {itemResults.length > 0 && (resultTypeFilter === undefined || resultTypeFilter === SearchResultTypeEnum.Item) && (
                                        <SectionGroup title={<div className="d-flex align-items-center"><i className="bi-upc-scan text-muted me-4 fs-2" />{Localization.SEARCH_BAR.ARTICOLI(itemResults.length)}</div>}>
                                            {itemResults.map(result=>{
                                                return <HeaderSearchBarResult key={`header_search_result_${result.id}`} result={result} />
                                            })}
                                        </SectionGroup>
                                    )}
                                    {orderResults.length > 0 &&  (resultTypeFilter === undefined || resultTypeFilter === SearchResultTypeEnum.Order) && (
                                        <SectionGroup title={<div className="d-flex align-items-center"><i className="bi-card-checklist text-muted me-4 fs-2" />{Localization.SEARCH_BAR.ORDINI(orderResults.length)}</div>}>
                                            {orderResults.map(result=>{
                                                return <HeaderSearchBarResult key={`header_search_result_${result.id}`} result={result} />
                                            })}
                                        </SectionGroup>
                                    )}
                                    {shopResults.length > 0 && (resultTypeFilter === undefined || resultTypeFilter === SearchResultTypeEnum.Shop) && (
                                        <SectionGroup title={<div className="d-flex align-items-center"><i className="bi-cart text-muted me-4 fs-2" />{Localization.SEARCH_BAR.SHOP(shopResults.length)}</div>}>
                                            {shopResults.map(result=>{
                                                return <HeaderSearchBarResult key={`header_search_result_${result.id}`} result={result} />
                                            })}
                                        </SectionGroup>
                                    )}
                                    {customerResults.length > 0 && (resultTypeFilter === undefined || resultTypeFilter === SearchResultTypeEnum.Customer) && (
                                        <SectionGroup title={<div className="d-flex align-items-center"><i className="bi-people text-muted me-4 fs-2" />{Localization.SEARCH_BAR.CLIENTI(customerResults.length)}</div>}>
                                            {customerResults.map(result=>{
                                                return <HeaderSearchBarResult key={`header_search_result_${result.id}`} result={result} />
                                            })}
                                        </SectionGroup>
                                    )}
                                </div>
                            </div>
                        </div>
                    }
                </div>
        )}
    </div>
    {isSearchResultsOpen && <div style={{position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 9000, backgroundColor: "rgba(0,0,0,0.3)"}} onClick={()=>{
        toggleIsSearchOpen(false)
    }} />}
    </>
}

export default HeaderSearchBar